'use client';

import { useState } from 'react';
import { AdminProfile, CompanyInfo } from '../../types';
import { COMPANY_INFO } from '../../data/mock-data';
import AdminHeader from '../shared/AdminHeader';
import AdminNav from '../shared/AdminNav';

interface SettingsPageProps {
  profile: AdminProfile;
  onNavigate: (page: 'dashboard' | 'receipts' | 'clients' | 'users' | 'settings') => void;
  onLogout: () => void;
  onGoToKiosk: () => void;
}

export default function SettingsPage({
  profile,
  onNavigate,
  onLogout,
  onGoToKiosk,
}: SettingsPageProps) {
  const [saving, setSaving] = useState(false);
  const [companyData, setCompanyData] = useState<CompanyInfo>({ ...COMPANY_INFO });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    // 데모용 저장 시뮬레이션
    setTimeout(() => {
      alert('회사 정보가 성공적으로 업데이트되었습니다.');
      setSaving(false);
    }, 500);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setCompanyData({
      ...companyData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminHeader profile={profile} onLogout={onLogout} onGoToKiosk={onGoToKiosk} />
      <AdminNav
        currentPage="settings"
        onNavigate={onNavigate}
        isSuperAdmin={profile.role === 'super_admin'}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-6">
              회사 정보 수정
            </h3>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    회사명 *
                  </label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    required
                    value={companyData.name}
                    onChange={handleChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm px-3 py-2 border"
                  />
                  <p className="mt-1 text-sm text-gray-500">전표에 표시될 회사명입니다.</p>
                </div>

                <div>
                  <label htmlFor="business_number" className="block text-sm font-medium text-gray-700">
                    사업자번호
                  </label>
                  <input
                    type="text"
                    name="business_number"
                    id="business_number"
                    value={companyData.business_number}
                    onChange={handleChange}
                    placeholder="123-45-67890"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm px-3 py-2 border"
                  />
                </div>

                <div>
                  <label htmlFor="contact_person" className="block text-sm font-medium text-gray-700">
                    담당자명 *
                  </label>
                  <input
                    type="text"
                    name="contact_person"
                    id="contact_person"
                    required
                    value={companyData.contact_person}
                    onChange={handleChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm px-3 py-2 border"
                  />
                  <p className="mt-1 text-sm text-gray-500">전표에 표시될 담당자 이름입니다.</p>
                </div>

                <div>
                  <label htmlFor="contact_phone" className="block text-sm font-medium text-gray-700">
                    담당자 연락처 *
                  </label>
                  <input
                    type="tel"
                    name="contact_phone"
                    id="contact_phone"
                    required
                    value={companyData.contact_phone}
                    onChange={handleChange}
                    placeholder="010-0000-0000"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm px-3 py-2 border"
                  />
                  <p className="mt-1 text-sm text-gray-500">전표에 표시될 연락처입니다.</p>
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                    주소
                  </label>
                  <textarea
                    name="address"
                    id="address"
                    rows={3}
                    value={companyData.address}
                    onChange={handleChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm px-3 py-2 border"
                  />
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                <p className="text-sm text-yellow-800">
                  <strong>참고:</strong> 회사 정보를 변경하면 새로 생성되는 전표부터 변경된 정보가 적용됩니다.
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => onNavigate('dashboard')}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  취소
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? '저장 중...' : '저장'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
